import { atom, computed } from 'nanostores';

import { clamp } from '@iconik-io/core/src/clamp';

import type {
  PositionedHighlight,
  Range,
  TimelineHighlight,
} from '../../types.js';
import type { createPlayableMediaStore } from './playable_media.js';
import type { createPlaylistStore } from './playlist.js';

/** Kept stable so hiding the highlights does not hand out a fresh array. */
const NO_HIGHLIGHTS: PositionedHighlight[] = [];

export function createTimelineStore<PlaylistItemMeta>(
  playlistStore: ReturnType<typeof createPlaylistStore<PlaylistItemMeta>>,
  playableMediaStore: ReturnType<typeof createPlayableMediaStore>,
) {
  const $highlights = atom<TimelineHighlight[]>([]);
  const $highlightsVisible = atom(true);

  const setHighlights = (highlights: TimelineHighlight[]) => {
    // Clearing an already-empty set is the common case, on every asset page
    // that was not opened from a search. A fresh `[]` fails the identity check
    // an atom bails out on, so it would re-render every subscriber for nothing.
    if (highlights.length === 0 && $highlights.get().length === 0) {
      return;
    }
    $highlights.set(highlights);
  };

  const setHighlightsVisible = (visible: boolean) => {
    $highlightsVisible.set(visible);
  };

  const $timelineDuration = computed(
    [playableMediaStore.$playableRange, playlistStore.$frameDuration],
    (playableRange, frameDuration): number | undefined => {
      return Math.max(
        playableRange.end - playableRange.start - frameDuration,
        0,
      );
    },
  );

  const timeToProgress = (time: number) => {
    const playableRangeStart = playableMediaStore.$playableRange.get().start;
    const timelineDuration = $timelineDuration.get();
    if (!timelineDuration) {
      return undefined;
    }
    return (time - playableRangeStart) / timelineDuration;
  };

  /**
   * The highlights in the track's own progress space, ready to draw: absolute
   * times converted, clamped to the track, and the ones that would draw nothing
   * dropped. Derived here rather than in the view so it is recomputed when the
   * playable range moves rather than on every playback frame.
   *
   * Holds them whether or not they are shown, so what a highlight covers can
   * still be answered while the spans are hidden.
   */
  const $allPositionedHighlights = computed(
    [$highlights, playableMediaStore.$playableRange, $timelineDuration],
    (highlights, playableRange, duration): PositionedHighlight[] => {
      if (!duration || highlights.length === 0) {
        return NO_HIGHLIGHTS;
      }
      return highlights.flatMap((highlight) => {
        const start = clamp(timeToProgress(highlight.start) ?? 0, 0, 1);
        const end = clamp(timeToProgress(highlight.end) ?? 0, 0, 1);
        if (end <= start) {
          return [];
        }
        return [
          {
            ...highlight,
            start,
            width: end - start,
            // Derived back from the clamped progress so the times always
            // describe the span that was actually drawn.
            range: {
              start: playableRange.start + start * duration,
              end: playableRange.start + end * duration,
            },
          },
        ];
      });
    },
  );

  /**
   * What to draw: empty while the highlights are hidden, which is the whole of
   * hiding them, since pressing and stepping then find nothing to select.
   */
  const $positionedHighlights = computed(
    [$allPositionedHighlights, $highlightsVisible],
    (highlights, visible) => (visible ? highlights : NO_HIGHLIGHTS),
  );

  /**
   * Whether a range is one a highlight covers, so a selection made from the
   * heatmap can be told apart from one the user drew.
   *
   * Compared in frames, which is the grain a selected range is stored at, and
   * within one frame: a highlight running to the end of the media reconstructs
   * its end from progress 1, and that is the last frame rather than the
   * playable end a range from a link is clamped to.
   */
  const isHighlightRange = (range: Range) => {
    const frameDuration = playlistStore.$frameDuration.get();
    const toFrame = (time: number) =>
      frameDuration > 0 ? Math.round(time / frameDuration) : time;
    const matches = (highlightTime: number, rangeTime: number) =>
      Math.abs(toFrame(highlightTime) - toFrame(rangeTime)) <= 1;
    return $allPositionedHighlights
      .get()
      .some(
        (highlight) =>
          matches(highlight.range.start, range.start) &&
          matches(highlight.range.end, range.end),
      );
  };

  /**
   * The highlight under a point on the track, if the point is on one.
   *
   * `minWidth`, in the same progress space, is the floor the view draws a
   * narrow span at. Without it a short match on a long video is pressable only
   * within the fraction of a pixel it really covers, not the marker on screen.
   */
  const findHighlightAt = (progress: number, minWidth = 0) =>
    $positionedHighlights
      .get()
      .find(
        (highlight) =>
          progress >= highlight.start &&
          progress <= highlight.start + Math.max(highlight.width, minWidth),
      );

  const $timelineCurrentTime = computed(
    [playableMediaStore.$playableRange, playableMediaStore.$currentTime],
    (playableRange, currentTime): number => {
      if (currentTime === undefined) {
        return 0;
      }
      return Math.max(currentTime - playableRange.start, 0);
    },
  );

  const $progress = computed(
    [$timelineCurrentTime, $timelineDuration],
    (currentTime, duration): number | undefined => {
      if (!duration) {
        return 0;
      }
      return (
        playableMediaStore.getFrameAlignedTime(currentTime) /
        playableMediaStore.getFrameAlignedTime(duration)
      );
    },
  );

  const progressToTime = (progress: number) => {
    const timelineDuration = $timelineDuration.get();
    if (!timelineDuration) {
      return undefined;
    }
    const playableRangeStart = playableMediaStore.$playableRange.get().start;
    return playableMediaStore.getFrameAlignedTime(
      progress * timelineDuration + playableRangeStart,
    );
  };

  // Take a time in seconds, return SMPTE that is offset aware
  const timeToSmpte = (time: number) => {
    const currentItem = playlistStore.$currentPlaylistItem.get();

    const seconds = time;
    const timecode = playlistStore.createTimecode({ seconds });
    if (currentItem?.firstFrameTimecode) {
      timecode.addFrames(playlistStore.$firstFrameOffset.get());
    }

    return timecode.toSMPTE();
  };

  // Take time in seconds, return frame number in 1 based count
  const timeToFrames = (time: number) => {
    return Math.round(time / playlistStore.$frameDuration.get()) + 1;
  };

  const $startTimecode = computed(
    [playableMediaStore.$playableRange, playlistStore.$currentPlaylistItem],
    (playableRange, currentItem) => {
      if (!currentItem) {
        return undefined;
      }

      const seconds = Math.max(playableRange.start, 0);
      const timecode = playlistStore.createTimecode({
        seconds,
      });
      return timecode;
    },
  );

  // Start SMPTE timecode, represented with offset if present.
  const $smpteStartTimecode = computed(
    [$startTimecode, playlistStore.$firstFrameOffset],
    (startTimecode, firstFrameOffset) => {
      if (!startTimecode) {
        return undefined;
      }

      const timecode = playlistStore.createTimecode({
        seconds: startTimecode.toSeconds(),
      });

      timecode.addFrames(firstFrameOffset);

      return timecode.toSMPTE();
    },
  );

  const $startFrame = computed([$startTimecode], (startTimecode) => {
    if (!startTimecode) {
      return undefined;
    }
    return startTimecode.toFrames();
  });

  // End timecode represents the last frame of the video, not the duration.
  const $endTimecode = computed(
    [
      playlistStore.$currentPlaylistItem,
      playableMediaStore.$lastPlayableFrameTime,
    ],
    (currentItem, lastFrameTime) => {
      if (!currentItem) {
        return undefined;
      }
      const seconds = lastFrameTime;
      const timecode = playlistStore.createTimecode({
        seconds,
      });
      return timecode;
    },
  );

  const $smpteEndTimecode = computed(
    [$endTimecode, playlistStore.$firstFrameOffset],
    (endTimecode, firstFrameOffset) => {
      if (!endTimecode) {
        return undefined;
      }

      const timecode = playlistStore.createTimecode({
        seconds: endTimecode.toSeconds(),
      });

      timecode.addFrames(firstFrameOffset);

      return timecode.toSMPTE();
    },
  );

  const $endFrame = computed([$endTimecode], (endTimecode) => {
    if (!endTimecode) {
      return undefined;
    }
    return endTimecode.toFrames();
  });

  return {
    $progress,
    $timelineDuration,
    $timelineCurrentTime,
    $highlights,
    $highlightsVisible,
    $positionedHighlights,
    findHighlightAt,
    isHighlightRange,
    setHighlights,
    setHighlightsVisible,
    progressToTime,
    timeToProgress,
    timeToSmpte,
    timeToFrames,
    $startTimecode,
    $endTimecode,
    $smpteStartTimecode,
    $smpteEndTimecode,
    $startFrame,
    $endFrame,
  };
}
